import {
  ClearPrivateSpaceHistory,
  GetPrivateSpace,
  HidePrivateSpace,
  reqClearPrivateSpaceHistory,
  reqGetPrivateSpace,
  reqHidePrivateSpace,
  reqSetPrivateSpaceUnreadMark,
  reqSyncPrivateSpace,
  SetPrivateSpaceUnreadMark,
  SyncPrivateSpace,
} from '@weblite/ts-action/private-space'
import { request, requestNow } from '$setup/ws'
import { removeMessagesByRange } from '$logic/message'
import { nothing } from '$func/utils'
import { updateRoomLastMessageDebounced } from '$logic/room-last-message'
import { showChatRemovedSnackbar } from '$comp/snackbars'
import { privateSpaceStorage } from './storage'
import * as sync from './offline-sync'
import * as roomService from '../room'

export const getPrivateSpace = privateSpaceStorage.get

export async function clearHistory(): Promise<void> {
  const { roomId } = await getPrivateSpace()
  const { iid } = await roomService.getRoom(roomId)
  const unsetSync = await sync.set({ firstVisibleIid: iid + 1 })
  await privateSpaceStorage.set({ firstVisibleIid: iid + 1 })
  await updateRoomLastMessageDebounced(roomId)
  removeMessagesByRange(roomId, 1, iid)

  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<ClearPrivateSpaceHistory>(reqClearPrivateSpaceHistory({ iid })).then(
    async res => {
      await privateSpaceStorage.set(res.payload)
      await unsetSync()
    },
  )
}

export async function hidePrivateSpace(
  shouldClearHistory: boolean,
  disableSnackbar = false,
): Promise<void> {
  const { roomId } = await getPrivateSpace()
  const { iid } = await roomService.getRoom(roomId)
  const unsetDeleteRoomSync = await roomService.addToDeletedRooms(roomId, {
    type: 'PRIVATE_SPACE',
    iid,
  })
  const unsetClearHistorySync = shouldClearHistory
    ? await sync.set({ firstVisibleIid: iid + 1 })
    : nothing
  await roomService.pruneRoom(roomId)
  if (shouldClearHistory) removeMessagesByRange(roomId, 1, iid)
  if (!disableSnackbar) showChatRemovedSnackbar()

  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<HidePrivateSpace>(
    reqHidePrivateSpace({ iid, clearHistory: shouldClearHistory }),
  ).then(() => {
    unsetDeleteRoomSync()
    unsetClearHistorySync()
  })
}

export async function setPrivateSpaceUnreadMark(unreadMark: boolean) {
  const privateSpace = await getPrivateSpace()
  if (privateSpace.unreadMark === unreadMark) return

  const unsetSync = await sync.set({ unreadMark })
  await privateSpaceStorage.set({ unreadMark })

  // NOTE: should resolve after changing in frontend for good offline experience. hence request has no await
  request<SetPrivateSpaceUnreadMark>(
    reqSetPrivateSpaceUnreadMark({ value: unreadMark }),
  ).then(async res => {
    await privateSpaceStorage.set(res.payload)
    await unsetSync()
  })
}

export async function markPrivateSpaceAsRead() {
  await setPrivateSpaceUnreadMark(false)
}

export async function syncPrivateSpace(): Promise<void> {
  if (!getPrivateSpace()) {
    const res = await request<GetPrivateSpace>(reqGetPrivateSpace({}))
    await privateSpaceStorage.set(res.payload)
    const { roomId } = await getPrivateSpace()
    await updateRoomLastMessageDebounced(roomId)
    return
  }
  const { item, del, release } = await sync.get()
  try {
    const res = await requestNow<SyncPrivateSpace>(
      reqSyncPrivateSpace({ fields: item }),
    )
    await privateSpaceStorage.set(res.payload.fields)
    const { roomId } = await getPrivateSpace()
    if (item && 'firstVisibleIid' in item)
      await updateRoomLastMessageDebounced(roomId)
    await del()
  } finally {
    release()
  }
}
